import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCw, Radar, Music2, Layers, Compass, Rocket } from "lucide-react";

export default function TrendRadar() {
  const [radar, setRadar] = useState(null);
  const [niche, setNiche] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.post("/radar/weekly", { niche: niche || undefined, force_refresh: force });
      setRadar(r.data);
      if (r.data.niche) setNiche(r.data.niche);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Radar failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">
            // trend radar
          </p>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter">
            This week in <span className="text-[#CCFF00]">{niche || "your niche"}</span>.
          </h1>
          <p className="mt-3 text-neutral-400 max-w-xl">
            AI-generated weekly report. Cached for 7 days. Ride these before saturation.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Override niche"
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
            className="h-10 bg-[#0e0e0e] border-white/10 w-40"
            data-testid="radar-niche-input"
          />
          <Button
            variant="ghost"
            onClick={() => load(true)}
            disabled={busy}
            className="rounded-full border border-white/15"
            data-testid="radar-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {busy && !radar ? (
        <p className="text-neutral-400 py-16 text-center animate-pulse">
          Claude is scanning your niche…
        </p>
      ) : radar ? (
        <div className="space-y-6" data-testid="radar-content">
          {/* Headline */}
          <div className="p-8 rounded-2xl bg-[#CCFF00] text-black">
            <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-3">
              // this week's headline
            </p>
            <p
              className="font-display font-black text-2xl md:text-3xl tracking-tight leading-tight"
              data-testid="radar-headline"
            >
              {radar.headline}
            </p>
          </div>

          {/* Experiment */}
          <div className="p-6 rounded-2xl bg-[#141414] border border-white/10">
            <div className="flex items-start gap-4">
              <Rocket className="w-6 h-6 text-[#CCFF00] shrink-0 mt-1" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                  // experiment to run this week
                </p>
                <p className="font-display font-bold text-lg leading-snug">{radar.experiment}</p>
              </div>
            </div>
          </div>

          {/* Audios */}
          <Section title="Emerging Audios" icon={Music2}>
            {radar.audios?.map((a, i) => (
              <RadarItem key={i} name={a.name} why={a.why} meta={a.estimated_uses} />
            ))}
          </Section>

          {/* Formats */}
          <Section title="Format Arbitrage" icon={Layers}>
            {radar.formats?.map((f, i) => (
              <RadarItem key={i} name={f.name} why={f.why} />
            ))}
          </Section>

          {/* Adjacent niches */}
          <Section title="Adjacent Niches to Try" icon={Compass}>
            {radar.adjacent_niches?.map((n, i) => (
              <RadarItem key={i} name={n.niche} why={n.angle} />
            ))}
          </Section>

          <p className="text-xs font-mono text-neutral-500 text-center pt-4">
            {radar.cached
              ? "Cached — next refresh Monday 09:00 UTC"
              : `Generated ${new Date(radar.created_at).toLocaleString()}`}
          </p>
        </div>
      ) : (
        <p className="text-neutral-500 text-center py-16">No radar yet.</p>
      )}
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#CCFF00]" />
      <h2 className="font-mono text-xs uppercase tracking-widest text-neutral-400">{title}</h2>
    </div>
    <div className="grid md:grid-cols-3 gap-3">{children}</div>
  </div>
);

const RadarItem = ({ name, why, meta }) => (
  <div className="p-5 rounded-2xl bg-[#141414] border border-white/5 hover:border-white/15 transition-colors">
    <p className="font-display font-bold text-lg leading-tight mb-2">{name}</p>
    <p className="text-sm text-neutral-400 leading-relaxed">{why}</p>
    {meta && <p className="mt-3 font-mono text-[10px] text-[#CCFF00] uppercase tracking-widest">{meta}</p>}
  </div>
);
